import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

import { handoffRedirectTarget } from '../../auth/oauthHandoff';
import config from '../../config';
import { errorText, formatWhen } from '../labels';
import type { SignupPreview } from '../types';

// The landing (plan §6, §16): what the link is, and the one explicit click that claims it (D10 — page
// load never claims, so a mail scanner opening the link changes nothing).

interface Props {
    preview: SignupPreview;
    notice: string | null;
    busy: boolean;
    error: unknown;
    onStart: () => void;
}

const CONTACT = 'Ask your Curated Planet contact for a new link.';

/** Every preview state has its own landing (signupContract.test.js holds this to the contract). */
export const LANDING_STATES = ['available', 'yours', 'claimed_elsewhere', 'used', 'expired', 'revoked'];

export default function Landing({ preview, notice, busy, error, onStart }: Props) {
    const { invite } = preview;
    const signInUrl = handoffRedirectTarget(preview.sign_in_url, { allowHttp: config.env === 'dev' });

    let title: string;
    let body: React.ReactNode;
    let action: React.ReactNode = null;
    switch (preview.state) {
        case 'available':
            title = 'Welcome to TourOptima';
            body = (
                <>
                    <Typography paragraph>
                        Set up your TourOptima account in a few minutes: connect your booking system, check your company
                        details and choose your password.
                    </Typography>
                    {invite.bound && invite.email_masked && (
                        <Typography paragraph>
                            You will sign in with <strong>{invite.email_masked}</strong>.
                        </Typography>
                    )}
                    {invite.expires_ts && (
                        <Typography variant="body2" color="text.secondary" paragraph>
                            This invitation is valid until {formatWhen(invite.expires_ts)}.
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        Starting reserves this link for this browser. You can move to another device at any time.
                    </Typography>
                </>
            );
            action = (
                <Button variant="contained" size="large" onClick={onStart} disabled={busy}
                    startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
                    Start onboarding
                </Button>
            );
            break;
        case 'yours':
            title = 'Welcome back';
            body = (
                <Typography paragraph>
                    You started setting up your account in this browser
                    {preview.claimed_since ? ` on ${formatWhen(preview.claimed_since)}` : ''}. Continue where you left off.
                </Typography>
            );
            action = (
                <Button variant="contained" size="large" onClick={onStart} disabled={busy}
                    startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
                    Continue
                </Button>
            );
            break;
        case 'claimed_elsewhere':
            title = 'This link is in use on another device';
            body = (
                <>
                    <Typography paragraph>
                        The set-up was started in another browser
                        {preview.claimed_since ? ` on ${formatWhen(preview.claimed_since)}` : ''}. Only one browser can use the
                        link at a time.
                    </Typography>
                    <Typography paragraph>
                        To continue here, open the link in the browser where you started and choose{' '}
                        <strong>Continue on another device</strong>, then open the link here again. If you no longer have that
                        browser, ask your Curated Planet contact to release the link.
                    </Typography>
                </>
            );
            break;
        case 'used':
            title = 'Your account is ready';
            body = (
                <>
                    <Typography paragraph>
                        Your TourOptima account has been created. Sign in to your dashboard with the e-mail address and password
                        you chose.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Forgot your password? Choose <strong>Forgot password?</strong> on the sign-in page.
                    </Typography>
                </>
            );
            action = signInUrl ? (
                <Button variant="contained" size="large" href={signInUrl}>Sign in</Button>
            ) : null;
            break;
        case 'expired':
            title = 'This invitation has expired';
            body = <Typography paragraph>{CONTACT}</Typography>;
            break;
        case 'revoked':
        default:
            title = 'This invitation is no longer valid';
            body = <Typography paragraph>{CONTACT}</Typography>;
            break;
    }

    return (
        <Box data-testid={`landing-${preview.state}`}>
            {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
            <Typography variant="h5" component="h1" gutterBottom>{title}</Typography>
            {invite.label && (
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Invitation for {invite.label}
                </Typography>
            )}
            <Box sx={{ mt: 2 }}>{body}</Box>
            {!!error && <Alert severity="error" sx={{ mt: 2 }}>{errorText(error)}</Alert>}
            {action && <Box sx={{ mt: 3 }}>{action}</Box>}
        </Box>
    );
}
